import { useRef, type ReactNode } from "react";

import { useActiveIndex } from "@/hooks/use-active-index";
import { cn } from "@/lib/utils";

/**
 * The tutorial's shape: chapters of numbered steps down the left, and beside them a preview
 * that stays put while the steps scroll past and changes to match the step under the reader's
 * eyes -- the step nearest a line about two fifths of the way down the screen. The step being
 * read is at full strength; the others recede. On a narrow screen the preview cannot sit
 * beside the text, so each step carries its own copy below it.
 *
 * Nothing here is content. A chapter is data -- title, intro, steps, and a function from the
 * active step to what the preview shows -- so the four chapters read as prose in their own
 * files.
 */

export interface Step {
  title: string;
  body: ReactNode;
}

export interface Chapter {
  id: string;
  /** Short, for the sticky chapter nav. */
  label: string;
  title: string;
  intro: ReactNode;
  steps: Step[];
  /** What the preview shows while step `i` is active. */
  preview: (i: number) => ReactNode;
  /** Sits beside the chapter title -- a switch that changes the previews, say. */
  controls?: ReactNode;
}

export function Tutorial({ chapters, children }: { chapters: Chapter[]; children?: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const current = useActiveIndex(root, "chapter", 0.3);
  return (
    <div ref={root}>
      <nav
        aria-label="Chapters"
        className="bg-background/90 sticky top-14 z-30 -mx-4 border-b px-4 backdrop-blur sm:top-16"
      >
        <ol className="mx-auto flex max-w-6xl gap-1 overflow-x-auto py-2 text-sm">
          {chapters.map((chapter, i) => (
            <li key={chapter.id} className="shrink-0">
              <a
                href={`#${chapter.id}`}
                aria-current={i === current ? "location" : undefined}
                className={cn(
                  "inline-flex items-center gap-2 rounded-md px-2.5 py-1 transition-colors",
                  i === current ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="tabular-nums">{i + 1}</span>
                {chapter.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="space-y-24 pt-10">
        {chapters.map((chapter, i) => (
          <ChapterView key={chapter.id} index={i} chapter={chapter} />
        ))}
        {children}
      </div>
    </div>
  );
}

function ChapterView({ index, chapter }: { index: number; chapter: Chapter }) {
  const root = useRef<HTMLElement>(null);
  const active = useActiveIndex(root, "step", 0.42);
  return (
    <section
      id={chapter.id}
      ref={root}
      data-chapter={index}
      className="scroll-mt-28 sm:scroll-mt-32"
      aria-labelledby={`${chapter.id}-title`}
    >
      <header className="max-w-2xl">
        <p className="text-primary text-sm font-medium">Chapter {index + 1}</p>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <h2 id={`${chapter.id}-title`} className="text-2xl font-semibold tracking-tight">
            {chapter.title}
          </h2>
          {chapter.controls}
        </div>
        <p className="text-muted-foreground mt-2 text-sm leading-6">{chapter.intro}</p>
      </header>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-12">
        <ol className="min-w-0 space-y-10 lg:space-y-16 lg:pt-4 lg:pb-[25vh]">
          {chapter.steps.map((step, i) => (
            <li
              key={step.title}
              data-step={i}
              className={cn(
                "min-w-0 transition-opacity duration-300",
                i === active ? "opacity-100" : "lg:opacity-40",
              )}
            >
              <button
                type="button"
                className="grid w-full cursor-default gap-3 text-left sm:grid-cols-[2rem_minmax(0,1fr)]"
                onClick={(event) =>
                  event.currentTarget.closest("li")?.scrollIntoView({ block: "center", behavior: "smooth" })
                }
                aria-current={i === active ? "step" : undefined}
              >
                <span
                  className={cn(
                    "inline-flex size-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors",
                    i === active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                  )}
                >
                  {i + 1}
                </span>
                <span className="min-w-0 space-y-1.5">
                  <span className="block text-base font-semibold">{step.title}</span>
                  <span className="text-muted-foreground block text-sm leading-6">{step.body}</span>
                </span>
              </button>
              {/* The preview, inline, where there is no room beside the text. */}
              <div className="mt-4 min-w-0 sm:pl-11 lg:hidden">{chapter.preview(i)}</div>
            </li>
          ))}
        </ol>

        <div className="hidden min-w-0 lg:block">
          {/* Pinned just under the chapter nav, and never taller than what is left of the
              viewport: a long preview scrolls inside itself rather than pushing its point off
              the bottom of the screen. */}
          <div className="sticky top-32 max-h-[calc(100vh-9rem)] min-w-0 overflow-y-auto">{chapter.preview(active)}</div>
        </div>
      </div>
    </section>
  );
}
